import { type Action, createStore, type Store as StoreType } from 'react-sweet-state';

import {
	DEFAULT_INSTANCE_ID,
	type RovodalInstanceId,
	type RovodalKey,
	type RovodalStoreState,
} from './types';

const initialState: RovodalStoreState = {
	byKey: {},
};

export type RovodalActions = {
	/** Open the `instanceId` instance of `key`, closing any other open Rovodal (only one is ever open). */
	open: (
		key: RovodalKey,
		instanceId?: RovodalInstanceId,
		state?: unknown,
	) => Action<RovodalStoreState>;
	/** Close a specific instance, or — when no `key` is given — whatever Rovodal is open. State is kept. */
	close: (key?: RovodalKey, instanceId?: RovodalInstanceId) => Action<RovodalStoreState>;
	/** Replace `state` for the `instanceId` instance of `key` (creates a closed instance if absent). */
	setState: (
		key: RovodalKey,
		instanceId: RovodalInstanceId | undefined,
		state: unknown,
	) => Action<RovodalStoreState>;
	/** Remove the `instanceId` instance of `key`; drops the key bucket when empty. */
	remove: (key: RovodalKey, instanceId?: RovodalInstanceId) => Action<RovodalStoreState>;
};

/** Immutably write a single `instanceId` entry into the nested `byKey` map. */
const writeInstance = (
	byKey: RovodalStoreState['byKey'],
	key: RovodalKey,
	instanceId: RovodalInstanceId,
	entry: RovodalStoreState['byKey'][string][string],
): RovodalStoreState['byKey'] => ({
	...byKey,
	[key]: {
		...byKey[key],
		[instanceId]: entry,
	},
});

const upsert =
	(
		key: RovodalKey,
		instanceId: RovodalInstanceId,
		isOpen: boolean,
		state: unknown,
	): Action<RovodalStoreState> =>
	({ setState, getState }) => {
		setState({
			byKey: writeInstance(getState().byKey, key, instanceId, { key, instanceId, isOpen, state }),
		});
	};

/** Flip every open entry to closed (only one is ever open, but this is safe regardless). */
const closeAllOpen = (byKey: RovodalStoreState['byKey']): RovodalStoreState['byKey'] => {
	let next = byKey;
	for (const k of Object.keys(byKey)) {
		for (const i of Object.keys(byKey[k])) {
			if (byKey[k][i].isOpen) {
				next = writeInstance(next, k, i, { ...next[k][i], isOpen: false });
			}
		}
	}
	return next;
};

const actions: RovodalActions = {
	open:
		(key, instanceId = DEFAULT_INSTANCE_ID, state) =>
		({ setState, getState }) => {
			const byKey = closeAllOpen(getState().byKey);
			const existing = byKey[key]?.[instanceId];
			setState({
				byKey: writeInstance(byKey, key, instanceId, {
					key,
					instanceId,
					isOpen: true,
					state: state !== undefined ? state : existing?.state,
				}),
			});
		},

	close:
		(key, instanceId = DEFAULT_INSTANCE_ID) =>
		({ setState, getState }) => {
			if (key === undefined) {
				setState({ byKey: closeAllOpen(getState().byKey) });
				return;
			}
			const existing = getState().byKey[key]?.[instanceId];
			if (!existing) {
				return;
			}
			setState({
				byKey: writeInstance(getState().byKey, key, instanceId, { ...existing, isOpen: false }),
			});
		},

	setState:
		(key, instanceId = DEFAULT_INSTANCE_ID, state) =>
		(api) => {
			const existing = api.getState().byKey[key]?.[instanceId];
			upsert(key, instanceId, existing?.isOpen ?? false, state)(api);
		},

	remove:
		(key, instanceId = DEFAULT_INSTANCE_ID) =>
		({ setState, getState }) => {
			const byKey = getState().byKey;
			const bucket = byKey[key];
			if (!bucket || !(instanceId in bucket)) {
				return;
			}
			const { [instanceId]: _removed, ...remainingInstances } = bucket;
			if (Object.keys(remainingInstances).length === 0) {
				const { [key]: _removedBucket, ...restKeys } = byKey;
				setState({ byKey: restKeys });
				return;
			}
			setState({ byKey: { ...byKey, [key]: remainingInstances } });
		},
};

/**
 * The single, private Rovodal store singleton. Public hooks in sibling files
 * are created from this; it is not exposed via the package `exports`.
 */
export const RovodalStore: StoreType<RovodalStoreState, RovodalActions> = createStore({
	name: 'rovodal',
	initialState,
	actions,
});
